import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PersonActivityEditorFormComponent } from '../../components/person-event-editors/person-activity-editor-form.component';
import { PersonAppointmentEditorFormComponent } from '../../components/person-event-editors/person-appointment-editor-form.component';
import { PersonAssignmentEditorFormComponent } from '../../components/person-event-editors/person-assignment-editor-form.component';
import { PersonMinistryEditorFormComponent } from '../../components/person-event-editors/person-ministry-editor-form.component';
import { PersonReligiousEventEditorFormComponent } from '../../components/person-event-editors/person-religious-event-editor-form.component';
import { Person } from '../../models/person.model';
import { PersonEvent, PersonEventActivityOption, PersonEventActivityRoleOption, PersonEventEditorContext } from '../../models/person-event.model';
import { PersonEventService } from '../../services/person-event.service';
import { NotificationService } from '@shared/services/notification.service';
import { ReferenceItem } from '@features/user/models/filter-data.model';
import { PersonEventsContextService } from '../person-events-shell-page/person-events-context.service';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import {
  getPersonEventCategoryForKind,
  getPersonEventEditorErrorMessage,
  parsePersonEventEditorKind,
  PersonEventEditorKind,
  supportsPersonEventEditing,
} from './person-event-editor.utils';

const EMPTY_EDITOR_CONTEXT: PersonEventEditorContext = {
  units: [],
  communities: [],
  ministries: [],
  appointments: [],
  assignments: [],
  activities: [],
  activityRoles: [],
  currentMinisterialAreas: [],
};

@Component({
  selector: 'app-person-event-editor-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatIconModule,
    MatProgressSpinnerModule,
    PersonMinistryEditorFormComponent,
    PersonAppointmentEditorFormComponent,
    PersonAssignmentEditorFormComponent,
    PersonActivityEditorFormComponent,
    PersonReligiousEventEditorFormComponent,
  ],
  templateUrl: './person-event-editor-page.component.html',
  styleUrl: './person-event-editor-page.component.scss',
})
export class PersonEventEditorPageComponent implements OnInit, OnDestroy {
  person: Person | null = null;
  context: PersonEventEditorContext = EMPTY_EDITOR_CONTEXT;
  currentEvent: PersonEvent | null = null;
  events: PersonEvent[] = [];
  isLoading = true;
  errorMessage: string | null = null;
  canManageMinisterialActivity = false;

  private personId = '';
  private kind: PersonEventEditorKind | null = null;
  private eventId: number | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly notifications: NotificationService,
    private readonly personEventService: PersonEventService,
    private readonly eventsContext: PersonEventsContextService,
    private readonly userFullInfoService: UserFullInfoService,
  ) {}

  ngOnInit(): void {
    this.personId = this.eventsContext.personId || (this.route.snapshot.paramMap.get('person-id') ?? '');
    this.kind = parsePersonEventEditorKind(this.route.snapshot.paramMap.get('kind'));
    const eventIdParam = this.route.snapshot.paramMap.get('event-id');
    this.eventId = eventIdParam ? Number(eventIdParam) : null;

    if (!this.personId || !this.kind) {
      this.notifications.error('No se pudo abrir la pantalla solicitada.');
      this.navigateToEvents();
      return;
    }

    if (this.eventId !== null && !supportsPersonEventEditing(this.kind)) {
      this.notifications.error('Ese tipo de registro no se puede editar desde esta pantalla.');
      this.navigateToEvents();
      return;
    }

    this.person = this.eventsContext.person;
    if (!this.ensureMinisterialActivityAllowed(this.person)) {
      return;
    }

    this.eventsContext.person$
      .pipe(takeUntil(this.destroy$))
      .subscribe(person => {
        this.person = person;
        this.ensureMinisterialActivityAllowed(person);
        this.cdr.markForCheck();
      });

    this.userFullInfoService.getUserInfo$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.canManageMinisterialActivity = this.userFullInfoService
          .hasPermission('ManageMinisterialActivity', user);
        this.cdr.markForCheck();
      });

    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get ministries(): ReferenceItem[] {
    return this.context.ministries;
  }

  get appointments(): ReferenceItem[] {
    return this.context.appointments;
  }

  get assignments(): ReferenceItem[] {
    return this.context.assignments;
  }

  get activities(): PersonEventActivityOption[] {
    return this.context.activities;
  }

  get activityRoles(): PersonEventActivityRoleOption[] {
    return this.context.activityRoles;
  }

  get isMinistry(): boolean {
    return this.kind === 'ministry';
  }

  get isMinistryChange(): boolean {
    return this.kind === 'ministry-change';
  }

  get isMinisterialActivity(): boolean {
    return this.kind === 'ministerial-activity';
  }

  get isAppointment(): boolean {
    return this.kind === 'appointment';
  }

  get isAssignment(): boolean {
    return this.kind === 'assignment';
  }

  get isActivity(): boolean {
    return this.kind === 'activity';
  }

  get isReligiousEvent(): boolean {
    return this.kind === 'religious-event';
  }

  get religiousEvents(): PersonEvent[] {
    return this.events.filter(event => event.category === 'ReligiousEvent');
  }

  get appointmentEvents(): PersonEvent[] {
    return this.events.filter(event => event.eventKind === 'appointment');
  }

  get assignmentEvents(): PersonEvent[] {
    return this.events.filter(event => event.eventKind === 'assignment');
  }

  get activityEvents(): PersonEvent[] {
    return this.events.filter(event => event.eventKind === 'activity');
  }

  get ministryEvents(): PersonEvent[] {
    return this.events.filter(event => event.category === 'MinisterialActivity' && event.eventKind !== 'appointment' && event.eventKind !== 'assignment');
  }

  goBack(): void {
    this.navigateToEvents();
  }

  onSaved(): void {
    this.eventsContext.refreshPerson()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.goBack(),
        error: () => this.goBack(),
      });
  }

  private loadData(): void {
    this.isLoading = true;
    this.errorMessage = null;
    const requestKind = this.getRequestKind();

    forkJoin({
      context: this.shouldLoadEditorContext(requestKind)
        ? this.personEventService.getEditorContext(this.personId, requestKind)
        : of(this.createEmptyEditorContext()),
      events: this.personEventService.getEvents(
        this.personId,
        requestKind),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ context, events }) => {
          this.context = context;
          this.events = events;

          if (this.eventId !== null && this.kind) {
            const category = getPersonEventCategoryForKind(this.kind);
            this.currentEvent = events.find(event =>
              event.id === this.eventId &&
              (!category || event.category === category) &&
              (!this.kind || this.matchesEditorKind(event))
            ) ?? null;

            if (!this.currentEvent) {
              this.isLoading = false;
              this.errorMessage = 'No se encontro el registro que queres editar.';
              this.cdr.markForCheck();
              return;
            }
          }

          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.isLoading = false;
          this.errorMessage = getPersonEventEditorErrorMessage(err, 'No se pudo cargar la pantalla.');
          this.cdr.markForCheck();
        },
      });
  }

  private getRequestKind(): PersonEventEditorKind {
    return this.kind === 'ministry' || this.kind === 'ministry-change'
      ? 'ministerial-activity'
      : this.kind!;
  }

  private matchesEditorKind(event: PersonEvent): boolean {
    return this.kind === 'ministerial-activity'
      ? event.eventKind === 'ministerial-activity' || event.eventKind === 'officiant'
      : event.eventKind === this.kind;
  }

  private shouldLoadEditorContext(kind: PersonEventEditorKind): boolean {
    return kind !== 'religious-event';
  }

  private createEmptyEditorContext(): PersonEventEditorContext {
    return {
      units: [],
      communities: [],
      ministries: [],
      appointments: [],
      assignments: [],
      activities: [],
      activityRoles: [],
      currentMinisterialAreas: [],
    };
  }

  private ensureMinisterialActivityAllowed(person: Person | null): boolean {
    if (!this.isMinisterialActivity || !person || person.dateOfBirth) {
      return true;
    }

    this.notifications.error('Para registrar actividad ministerial, primero carga la fecha de nacimiento del miembro.');
    this.navigateToEvents();
    return false;
  }

  private navigateToEvents(): void {
    if (this.personId) {
      const allParams = this.route.snapshot.pathFromRoot.reduce(
        (acc, r) => ({ ...acc, ...r.params }), {} as Record<string, string>
      );
      const unitId = allParams['unit-id'];
      this.router.navigate(
        unitId
          ? ['/units', unitId, 'members', this.personId, 'events']
          : ['/people', this.personId, 'events']
      );
      return;
    }

    this.router.navigate(['/people']);
  }
}
